import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import { fetchPriceCheckDetails } from "../../service";
import Label from "../../common/Label";
import {
    formatHHMM,
    formatMoney,
    formatTimeTo12Hour,
} from "../../../../utils";
import useIsMobile from "../../../../common/hooks/useIsMobile";
import { getSettings } from "../../../../storage";

const PriceAllDetails = ({
    skill,
    jobRatePreHour,
    isHome = false,
    isCurrentJob = false,
    range = [],
    calculateFutureTime,
    eta=null
}) => {
    const isMobile = useIsMobile();
    const {
        customer_web_job_step_flow_is_enable: stepFlowIsEnable,
    } = getSettings();

    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState([]);

    // -----------------------------
    // Fetch Data
    // -----------------------------
    useEffect(() => {
        const loadPriceCheckDetails = async () => {
            try {
                const res = await fetchPriceCheckDetails("PRICE");

                if (res?.status === "Success") {
                    setDetails(res.price_check_details || []);
                }
            } catch (error) {
                console.error("Error fetching price check details:", error);
            } finally {
                setLoading(false);
            }
        };

        loadPriceCheckDetails();
    }, []);

    // -----------------------------
    // Computed Values
    // -----------------------------
    const isAddMxAndColor = !isHome && !isCurrentJob;
    const isAddBgColor = isHome && stepFlowIsEnable;

    const cardClassName = useMemo(
        () =>
            clsx(
                "card card-frame mb-3",
                isAddMxAndColor && "job-card-bg-color mx-3",
                isAddBgColor && "mt-3 job-card-bg-color-hash",
                isMobile && "mt-3"
            ),
        [isAddMxAndColor, isAddBgColor, isMobile]
    );

    const cardStyle = useMemo(() => {
        if (!isCurrentJob && stepFlowIsEnable && !isHome) {
            return {
                maxHeight: "380px",
                overflow: "auto",
            };
        }

        if (!isCurrentJob && stepFlowIsEnable) {
            return {
                maxHeight: "380px",
                overflowY: "auto",
                overflowX: "hidden",
            };
        }

        return {};
    }, [isCurrentJob, isHome, stepFlowIsEnable]);

    const hourlyRate = useMemo(() => {
        const rate = isCurrentJob
            ? jobRatePreHour ?? 0
            : jobRatePreHour?.rate_per_hour_with_company_commission ?? 0;

        return formatMoney(rate);
    }, [isCurrentJob, jobRatePreHour]);

    const [formattedStart, formattedEnd] = useMemo(() => {
        if (
            !range?.length ||
            range.length < 2 ||
            !calculateFutureTime
        ) {
            return ["--:--", "--:--"];
        }

        return [
            formatHHMM(calculateFutureTime(range[0])),
            formatHHMM(calculateFutureTime(range[1])),
        ];
    }, [range, calculateFutureTime]);

    const filteredDetails = useMemo(() => {
        return details.filter((item) => {
            const title = item?.price_check_detail_name || "";

            return !title.includes("[SKILL]") || !!skill?.skill_name;
        });
    }, [details, skill]);

    // -----------------------------
    // Helpers
    // -----------------------------
    const transformTitle = (title = "") =>
        title
            .replace("[SKILL]", skill?.skill_name ?? "")
            .replace("[RATE_PRE_HOUR]", hourlyRate);

    // -----------------------------
    // Render
    // -----------------------------
    if (loading || !filteredDetails.length) return stepFlowIsEnable ? <p className="text-center mt-5">Loading...</p> : null;

    return (
        <div className={cardClassName} style={cardStyle}>
            <div className="card-body">
                {filteredDetails.map((item, index) => (
                    <div
                        key={index}
                        className={index > 0 ? "mt-3" : ""}
                    >
                        <Label
                            text={transformTitle(
                                item.price_check_detail_name
                            )}
                        />

                        {item.price_check_detail_is_add_arrival_window === 1 && (
                            <p className="mb-0 text-sm text-muted">
                                Arrival window:{" "}
                                <b>
                                    {isCurrentJob ? formatHHMM(eta.job_eta_start_time, true) : formatTimeTo12Hour(formattedStart)} -{" "}
                                    {isCurrentJob ? formatHHMM(eta.job_eta_end_time, true) : formatTimeTo12Hour(formattedEnd)}
                                </b>
                            </p>
                        )}

                        {item.price_check_points?.map((point, idx) => (
                            <p
                                key={idx}
                                className={clsx(
                                    "mb-0 text-sm",
                                    idx > 0 && "mt-2"
                                )}
                            >
                                {point.price_check_point_description}
                            </p>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PriceAllDetails;